import React, { useCallback } from 'react'
import mapValues from 'lodash/mapValues'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { Modal, Form, message } from 'antd'
import { Formik, useFormikContext } from 'formik'
import { formElementPropsGenerator } from '../../lib/forms'
import { Yup } from '../../lib/validations'
import DeviceModelSelect from '../Forms/DeviceModelSelect'
import { Input, Checkbox } from '../Forms'
import api from '../../api'

const LABELS = {
  deviceModelId: 'Model',
  serialNumber: 'Serial number',
  modelType: 'Device Type',
  current: 'Is current device?',
}
const PLACEHOLDERS = {
  deviceModelId: 'Select a model',
  serialNumber: 'e.g. 123-456-789',
  current: 'Is this the current device?',
  modelType: '',
}

const INITIAL_VALUES = mapValues(LABELS, () => undefined)

const VALIDATION_SCHEMA = Yup.object().shape({
  deviceModelId: Yup.string().required(),
  serialNumber: Yup.string().required(),
  modelType: Yup.string().optional(),
  current: Yup.boolean().optional(),
})

const propsFor = formElementPropsGenerator({
  formName: 'device-form',
  labels: LABELS,
  placeholders: PLACEHOLDERS,
})

export default function SetDeviceModal({
  patientId,
  onDeviceChanged,
  visible,
  deviceToEdit,
}) {
  const saveDevice = useCallback(
    async dataPayload => {
      if (deviceToEdit.id) {
        await api.devices.updateDevice(deviceToEdit.id, dataPayload)
      } else {
        await api.patients.createDevice(patientId, dataPayload)
      }
    },
    [deviceToEdit, patientId],
  )

  const handleSave = useCallback(
    async ({ deviceModelId, serialNumber, current }) => {
      const success = deviceToEdit.id
        ? 'Device updated successfully'
        : 'Device created successfully'
      const errorMessage = deviceToEdit.id
        ? 'There was an error updating the device'
        : 'There was an error creating the device'
      try {
        await saveDevice({ deviceModelId, serialNumber, current })
        message.success(success)
        onDeviceChanged(true)
      } catch (e) {
        message.error(`${errorMessage}: ${e.message}`)
        onDeviceChanged(false)
      }
    },
    [saveDevice, onDeviceChanged, deviceToEdit],
  )
  const handleCancel = useCallback(() => onDeviceChanged(false), [
    onDeviceChanged,
  ])

  const DeviceModelSelectWrapper = props => {
    const { setFieldValue } = useFormikContext()

    return (
      <DeviceModelSelect
        {...props}
        onChange={value => {
          api.deviceModels.get(value).then(model => {
            setFieldValue('deviceModelId', value)
            setFieldValue('modelType', model.deviceType.name)
          })
        }}
      />
    )
  }

  const initialValues = {
    ...INITIAL_VALUES,
    deviceModelId: deviceToEdit?.deviceModelId,
    serialNumber: deviceToEdit?.serialNumber,
    modelType: deviceToEdit?.modelType,
    current: deviceToEdit?.current || false,
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSave}
      validationSchema={VALIDATION_SCHEMA}
    >
      {({ isSubmitting, values }) => (
        <Modal
          title="Change Device"
          visible={visible}
          onOk={() => handleSave(values)}
          onCancel={handleCancel}
          okText="Save device"
          confirmLoading={isSubmitting}
          centered
          destroyOnClose
        >
          <Form>
            <DeviceModelSelectWrapper
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...propsFor('deviceModelId')}
              autoFocus
            />
            <Input {...propsFor('modelType')} disabled />
            <Input {...propsFor('serialNumber')} />
            <Checkbox {...propsFor('current')} />
          </Form>
        </Modal>
      )}
    </Formik>
  )
}

SetDeviceModal.propTypes = {
  patientId: PropTypes.string.isRequired,
  onDeviceChanged: PropTypes.func,
  visible: PropTypes.bool,
  deviceToEdit: PropTypes.shape({
    id: PropTypes.string,
  }),
}

SetDeviceModal.defaultProps = {
  onDeviceChanged: noop,
  visible: false,
  deviceToEdit: {},
}
